/**
 * Qué conjunto describen unos criterios, en dos formas: si hay algún filtro, y un texto corto.
 *
 * En su propio módulo porque lo usan la app y la vista de diseño, y porque "sin filtros" es una
 * afirmación de dinero: si aquí faltara un criterio, el botón diría "Ver todo lo pendiente" y el
 * alcance "sin filtros" mientras se estaba filtrando de verdad — la peor confusión posible: creer
 * que se va a pagar todo, o al contrario.
 *
 * `programa` NO cuenta como filtro: dice qué universo se mira, no qué parte de él. Y `seleccion`
 * tampoco se mira: el front dejó de emitirlo el 2026-09-10 (ver `seleccion.ts`).
 */
import type { Criterios } from '@caes-payments/shared';

import { describirCriterioBlacklist } from './blacklist';

export function sinFiltros(c: Criterios): boolean {
  return (
    !c.regionName?.length &&
    !c.caesMitecoStatus?.length &&
    !c.rangoFechas &&
    !c.driveFileName?.length &&
    !c.certificationPeriod?.length &&
    !c.caesSoftKillerReason?.length &&
    // Booleano de tres estados: `false` (excluir) también es un filtro, así que se mira la presencia.
    c.isDriverBlacklisted === undefined
  );
}

/** Texto corto que dice a qué conjunto corresponden las cifras que se están viendo. */
export function describirAlcance(c: Criterios): string {
  if (sinFiltros(c)) return 'todo lo pendiente, sin filtros';
  const partes: string[] = [];
  if (c.regionName?.length) partes.push(`${c.regionName.length} CA`);
  if (c.rangoFechas) partes.push(`${c.rangoFechas.desde} a ${c.rangoFechas.hasta}`);
  if (c.caesMitecoStatus?.length) partes.push(`${c.caesMitecoStatus.length} estado MITECO`);
  if (c.certificationPeriod?.length) {
    partes.push(`${c.certificationPeriod.length} periodo de certificacion`);
  }
  if (c.driveFileName?.length) partes.push(`${c.driveFileName.length} fichero de certificacion`);
  if (c.caesSoftKillerReason?.length) {
    partes.push(`${c.caesSoftKillerReason.length} motivo de soft killer`);
  }
  const blacklist = describirCriterioBlacklist(c.isDriverBlacklisted);
  if (blacklist) partes.push(blacklist);
  return partes.join(' · ');
}

/**
 * Si dos juegos de criterios filtran lo mismo. Sirve para avisar cuando Ops va a confirmar un preview
 * cuyos filtros ya no son los de la pantalla: el confirm paga el PREVIEW (solo viaja `previewId`), así
 * que un filtro tocado después y no consultado no cambia lo que se paga, pero sí lo que Ops cree.
 *
 * Canónica: el orden de las claves y el de los valores de una lista no cambian el filtro (elegir
 * Galicia y luego Aragón es lo mismo que al revés). Una lista vacía y una ausente tampoco, porque el
 * contrato las trata igual — aunque el front nunca manda listas vacías.
 */
export function mismosCriterios(a: Criterios, b: Criterios): boolean {
  return JSON.stringify(canonica(a)) === JSON.stringify(canonica(b));
}

function canonica(valor: unknown): unknown {
  if (Array.isArray(valor)) {
    return valor.map(canonica).sort((x, y) => (JSON.stringify(x) < JSON.stringify(y) ? -1 : 1));
  }
  if (valor !== null && typeof valor === 'object') {
    return Object.fromEntries(
      Object.entries(valor)
        .filter(([, v]) => v !== undefined && !(Array.isArray(v) && v.length === 0))
        .sort(([x], [y]) => (x < y ? -1 : 1))
        .map(([k, v]) => [k, canonica(v)]),
    );
  }
  return valor;
}
